import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const apiPrefix = window.location.hostname === 'localhost' ? '' : '/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null); // null = still checking

    // Global 401 interceptor — catches expired sessions on any API call.
    // Uses window.location (full reload) because AuthProvider is outside BrowserRouter.
    useEffect(() => {
        const interceptorId = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    const url = error.config?.url || '';
                    // Don't intercept login/auth-check calls to avoid redirect loops.
                    if (!url.includes('/login') && !url.includes('/check_authentication')) {
                        setUser(null);
                        setIsAuthenticated(false);
                        sessionStorage.setItem('sessionExpired', '1');
                        window.location.href = '/login';
                    }
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptorId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshAuth = async () => {
        try {
            const authRes = await axios.get(`${apiPrefix}/check_authentication`, { withCredentials: true });
            if (authRes.data.message === 'User is authenticated') {
                const userRes = await axios.get(`${apiPrefix}/user_data`, { withCredentials: true });
                setUser(userRes.data);
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
        } catch (_) {
            setIsAuthenticated(false);
        }
    };

    useEffect(() => {
        refreshAuth();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const logout = async () => {
        try {
            await axios.post(`${apiPrefix}/logout`, null, { withCredentials: true });
        } catch (_) {}
        setUser(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, logout, refreshAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
